import React, { useState, useEffect } from 'react';
import {
  Menu, X, ChevronDown, Code, Smartphone, ShoppingCart, Brain, Palette,
  Cloud, Network, Shield, Boxes, Database, Users, Package, HeartPulse,
  GraduationCap, ShoppingBag, FileText, Bot, LayoutDashboard, Stethoscope,
  Building2, Truck, Plane, Factory, Film, DollarSign, ArrowRight,
  Info, Lightbulb, BookOpen, Printer, Server, Phone, Mail, Globe
} from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { name: 'Web Development', icon: Code, link: '/web-development' },
    { name: 'Mobile Apps', icon: Smartphone, link: '/mobile-app-development' },
    { name: 'E-commerce', icon: ShoppingCart, link: '/ecommerce-solutions' },
    { name: 'AI Development', icon: Brain, link: '/ai-ml-solutions' },
    { name: 'UI/UX Design', icon: Palette, link: '/ui-ux-design' },
    { name: 'Hosting', icon: Server, link: '/hosting' },
    { name: 'Cloud & DevOps', icon: Cloud, link: '/cloud-devops' },
    { name: 'API Integration', icon: Network, link: '/api-integration' },
    { name: 'QA & Testing', icon: Shield, link: '/qa-testing' },
    { name: 'Product Engineering', icon: Boxes, link: '/enterprise-solutions' }
  ];

  const solutions = [
    { name: 'ERP', icon: Database, link: '/erp-solution' },
    { name: 'CRM', icon: Users, link: '/crm-solution' },
    { name: 'HRMS', icon: Package, link: '/hrm-solution' },
    { name: 'Hospital Management', icon: HeartPulse, link: '/hospital-management' },
    { name: 'LMS', icon: GraduationCap, link: '/lms-solution' },
    { name: 'POS', icon: ShoppingBag, link: '/pos-solution' },
    { name: 'CMS', icon: FileText, link: '/cms-solution' },
    { name: 'Inventory Management', icon: Boxes, link: '/inventory-management' },
    { name: 'AI Chatbot', icon: Bot, link: '/ai-chatbot' },
    { name: 'Business Dashboard', icon: LayoutDashboard, link: '/business-dashboard' },
    { name: 'Printing', icon: Printer, link: '/printing-solution' }
  ];

  const industries = [
    { name: 'Healthcare', icon: Stethoscope, link: '/healthcare' },
    { name: 'E-commerce', icon: ShoppingCart, link: '/ecommerce-solutions' },
    { name: 'Banking & Finance', icon: DollarSign, link: '/finance-insurance' },
    { name: 'Manufacturing', icon: Factory, link: '/manufacturing' },
    { name: 'Education', icon: GraduationCap, link: '/education' },
    { name: 'Real Estate', icon: Building2, link: '/real-estate' },
    { name: 'Logistics', icon: Truck, link: '/logistics' },
    { name: 'Travel & Hospitality', icon: Plane, link: '/travel-hospitality' },
    { name: 'Media', icon: Film, link: '/media-entertainment' },
    { name: 'Enterprise', icon: Building2, link: '/enterprise-industry' }
  ];

  const insightsItems = [
    { name: 'About Us', icon: Info, link: '/about-us' },
    { name: 'Blogs & Tech Insights', icon: Lightbulb, link: '/insights' },
    { name: 'Projects & Solutions', icon: Code, link: '/projects' },
    { name: 'Careers & Hiring', icon: BookOpen, link: '/careers' }
  ];

  const dropdownPreviews = {
    Services: {
      image: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=700&q=80',
      badge: 'Featured Services',
      title: 'Full-Cycle Web Engineering',
      desc: 'Build high-performance web applications and cloud architectures.',
      link: '/web-development'
    },
    Solutions: {
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=700&q=80',
      badge: 'Enterprise Software',
      title: 'Custom ERP & CRM Systems',
      desc: 'Automate business workflows with intelligent software solutions.',
      link: '/erp-solution'
    },
    Industries: {
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=700&q=80',
      badge: 'Industry Solutions',
      title: 'Domain-Specific Expertise',
      desc: 'Software tailored for FinTech, Healthcare, Retail, & Logistics.',
      link: '/healthcare'
    },
    Insights: {
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700&q=80',
      badge: 'Company & Culture',
      title: 'About Our Team & Work',
      desc: 'Discover our agency story, client case studies, careers, and tech articles.',
      link: '/about-us'
    }
  };

  const getDropdownPosition = (name) => {
    if (name === 'Services') return 'left-0';
    if (name === 'Solutions') return '-left-20 xl:-left-32';
    if (name === 'Industries') return 'right-0 lg:-right-12 xl:right-0';
    if (name === 'Insights') return 'right-0';
    return 'left-0';
  };

  const navItems = [
    { name: 'Home', link: '/' },
    { name: 'Services', items: services },
    { name: 'Solutions', items: solutions },
    { name: 'Industries', items: industries },
    { name: 'Technologies', link: '/technologies' },
    { name: 'Insights', items: insightsItems },
    { name: 'Contact', link: '/contact' }
  ];

  const handleMouseEnter = (itemName) => {
    setOpenDropdown(itemName);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-gray-950/95 backdrop-blur-md shadow-lg ${isScrolled
        ? 'shadow-2xl'
        : ''
        }`}
    >
      {/* Sleek Bottom Cyan & Orange Gradient Border Line (Matching Footer) */}
      <div className="absolute bottom-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-cyan-500 via-blue-500 via-amber-500 to-orange-500 z-20 opacity-90" />
      {/* Top Contact Strip */}
      <div className="bg-gray-900/90 border-b border-gray-800/80 text-gray-300 text-[11px] sm:text-xs py-1.5 px-4 sm:px-6">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center justify-between w-full lg:w-auto space-x-2 sm:space-x-4 whitespace-nowrap">
            <a href="tel:+918796118101" className="flex items-center space-x-1.5 hover:text-orange-400 transition-colors whitespace-nowrap">
              <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-orange-400 flex-shrink-0" />
              <span className="whitespace-nowrap font-medium">+91 8796 118 101</span>
            </a>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <a href="mailto:info@hoasp.com" className="flex items-center space-x-1.5 hover:text-orange-400 transition-colors whitespace-nowrap">
              <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-cyan-400 flex-shrink-0" />
              <span className="whitespace-nowrap font-medium">info@hoasp.com</span>
            </a>
          </div>
          <div className="hidden lg:flex items-center space-x-2 text-gray-300 font-medium whitespace-nowrap">
            <span className="text-gray-600">|</span>
            <Globe className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
            <span className="whitespace-nowrap">Innovative Solutions for Every Business</span>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="container mx-auto px-6 py-2 md:py-2.5">
        <div className="flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2.5 group">
            <img
              src="/images/logohoasp_white.png"
              alt="HOASP International"
              className="h-10 md:h-11 w-auto object-contain group-hover:scale-105 transition-transform"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item, index) => (
              <div
                key={index}
                className="relative"
                onMouseEnter={() => item.items && handleMouseEnter(item.name)}
                onMouseLeave={handleMouseLeave}
              >
                {item.items ? (
                  <div>
                    <button
                      className={`flex items-center space-x-1 px-3.5 py-1.5 text-sm md:text-sm font-semibold transition-colors rounded-lg ${openDropdown === item.name
                        ? 'text-orange-400 bg-gray-900 border border-gray-800'
                        : 'text-gray-300 hover:text-orange-400 hover:bg-gray-900'
                        }`}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-orange-400" />
                    </button>

                    {/* Compact Mega Dropdown Menu */}
                    {openDropdown === item.name && (
                      <div className={`absolute top-full pt-1.5 z-50 ${getDropdownPosition(item.name)}`}>
                        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-3.5 shadow-2xl flex gap-4 w-[600px] xl:w-[640px] text-left">

                          {/* Left Menu Items Grid */}
                          <div className={`grid ${item.items.length > 5 ? 'grid-cols-2' : 'grid-cols-1'} gap-1 flex-1`}>
                            {item.items.map((subItem, subIndex) => (
                              <Link
                                key={subIndex}
                                to={subItem.link}
                                className="flex items-center space-x-2.5 py-1.5 px-2.5 rounded-lg hover:bg-orange-500/10 text-gray-300 hover:text-orange-400 transition-colors group/link"
                                onClick={() => setOpenDropdown(null)}
                              >
                                <subItem.icon className="w-4 h-4 text-cyan-400 flex-shrink-0 group-hover/link:scale-110 group-hover/link:text-orange-400 transition-all duration-200" />
                                <span className="text-sm font-semibold text-gray-200 group-hover/link:text-orange-400 leading-tight transition-colors">
                                  {subItem.name}
                                </span>
                              </Link>
                            ))}
                          </div>

                          {/* Right Featured Image Card */}
                          {dropdownPreviews[item.name] && (
                            <div className="hidden md:flex flex-col w-48 rounded-xl overflow-hidden bg-gray-950 border border-gray-800 relative group/card flex-shrink-0">
                              <div className="relative h-24 overflow-hidden">
                                <img
                                  src={dropdownPreviews[item.name].image}
                                  alt={dropdownPreviews[item.name].title}
                                  className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-300"
                                />
                                <span className="absolute top-1.5 left-1.5 bg-orange-500 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shadow-sm">
                                  {dropdownPreviews[item.name].badge}
                                </span>
                              </div>
                              <div className="p-2.5 flex-1 flex flex-col justify-between">
                                <div>
                                  <h4 className="text-sm font-bold text-white mb-0.5 leading-snug">
                                    {dropdownPreviews[item.name].title}
                                  </h4>
                                  <p className="text-[11px] text-gray-400 leading-tight">
                                    {dropdownPreviews[item.name].desc}
                                  </p>
                                </div>
                                <Link
                                  to={dropdownPreviews[item.name].link}
                                  onClick={() => setOpenDropdown(null)}
                                  className="inline-flex items-center text-[11px] font-bold text-orange-400 hover:text-orange-300 mt-1.5 gap-1 group/btn"
                                >
                                  <span>Explore</span>
                                  <ArrowRight className="w-3 h-3 group-hover/btn:translate-x-1 transition-transform" />
                                </Link>
                              </div>
                            </div>
                          )}

                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.link}
                    className="px-3.5 py-1.5 text-sm md:text-sm font-semibold text-gray-300 hover:text-orange-400 transition-colors rounded-lg hover:bg-gray-900"
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 text-sm md:text-sm font-bold rounded-xl transition-colors shadow-md">
                Get Consultation
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-1.5 text-gray-300 hover:text-white"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden absolute top-full left-0 right-0 w-full bg-[#111827] border-b-2 border-gray-700 max-h-[calc(100vh-70px)] overflow-y-auto shadow-2xl px-4 pt-2 pb-4 text-left space-y-2 z-50"
            style={{ backgroundColor: '#111827', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
          >
            {navItems.map((item, index) => (
              <div key={index} className="text-left pb-1 border-b border-gray-800/80 last:border-0">
                {item.items ? (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-extrabold text-orange-400 py-0.5 mb-1">
                      {item.name}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {item.items.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          to={subItem.link}
                          className="flex items-center space-x-2.5 py-1.5 px-2.5 rounded-md bg-[#1f2937] text-xs text-gray-100 font-semibold hover:bg-orange-500 hover:text-white transition-colors group/mobile"
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            setOpenDropdown(null);
                            window.scrollTo(0, 0);
                          }}
                        >
                          <subItem.icon className="w-3.5 h-3.5 text-cyan-400 group-hover/mobile:text-white flex-shrink-0 transition-colors" />
                          <span className="text-gray-100 group-hover/mobile:text-white transition-colors">{subItem.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.link}
                    className="block py-2 px-2.5 rounded-md bg-[#1f2937] text-sm font-bold text-white hover:bg-orange-500 hover:text-white transition-colors"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      window.scrollTo(0, 0);
                    }}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <div className="pt-1">
              <Link to="/contact" onClick={() => { setIsMobileMenuOpen(false); window.scrollTo(0, 0); }}>
                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm py-2.5 rounded-md transition-colors">
                  Get Consultation
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;